import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray, useFormikContext } from 'formik';
import {
  MultiSelectField,
  SocialMediaLinkInput,
  SelectInput,
  TextInput,
} from 'components/Form';
import AddCircle from '@material-ui/icons/AddCircle';
import RemoveCircle from '@material-ui/icons/RemoveCircle';
import { YEAR_OPTIONS } from 'utils/helper';
import { isCreativeProfile } from '../config';

const ServicesFields = () => (
  <>
    <div className="bg-twilight rounded-lg p-5 mb-8">
      <p className="font-semibold text-ebony mb-1">Your services</p>
      <p className="text-xs text-dusty">
        Add at least one service so we can match you with relevant members.
      </p>
    </div>
    <FieldArray
      name="users_services"
      render={({ push, remove, form }) => {
        const services = form.values.users_services;
        const activeServicesCount = services.filter(
          (service) => !service?._destroy
        ).length;

        return (
          <div>
            {services.map((service, index) => {
              if (service?._destroy) return null;

              const serviceNumber =
                services
                  .slice(0, index)
                  .filter((item) => !item?._destroy).length + 1;

              return (
                <div
                  className="border-b border-alto border-dashed mb-6 pb-4"
                  key={service.id || `service-${index + 1}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs uppercase tracking-wider text-dusty font-semibold">
                      Service {serviceNumber}
                    </p>
                    {activeServicesCount > 1 && (
                      <button
                        className="inline-flex items-center text-xs text-valencia focus:outline-none hover:opacity-75"
                        type="button"
                        onClick={() => {
                          if (service.id) {
                            form.setFieldValue(
                              `users_services[${index}]._destroy`,
                              true
                            );
                          } else {
                            remove(index);
                          }
                        }}
                      >
                        <RemoveCircle className="mr-1" fontSize="small" />
                        Delete service
                      </button>
                    )}
                  </div>
                  <Field
                    name={`users_services[${index}].name`}
                    component={TextInput}
                    label="Service name"
                    placeholder="e.g. Music production"
                    showRemaining
                    maxLength={100}
                  />
                  <Field
                    name={`users_services[${index}].description`}
                    component={TextInput}
                    label="Description"
                    placeholder="Describe how you can help other members"
                    type="textarea"
                    showRemaining
                    maxLength={500}
                  />
                </div>
              );
            })}
            <button
              className="text-easter flex items-center focus:outline-none hover:opacity-75 mb-8"
              type="button"
              onClick={() => push({ name: '', description: '' })}
            >
              <AddCircle className="mr-2" />
              Add another service
            </button>
          </div>
        );
      }}
    />
  </>
);

const WorkExperienceFields = ({ showExperienceFields, showWebsite }) => (
  <div className="bg-concrete rounded-lg p-5 mb-8">
    <p className="font-semibold text-ebony mb-1">
      {showExperienceFields ? 'Work experience & portfolio' : 'Website / portfolio'}
    </p>
    <p className="text-xs text-dusty mb-4">
      {showExperienceFields
        ? 'Add a role and use the website field for the related website or portfolio.'
        : 'Add a website or portfolio to your existing work experience.'}
    </p>
    <FieldArray
      name="users_experiences"
      render={({ push, remove, form }) => (
        <>
          {form.values.users_experiences.map((experience, index) => {
            if (experience?._destroy) return null;
            return (
              <div
                className="border-b border-alto border-dashed mb-6 pb-4"
                key={experience.id || `experience-${index + 1}`}
              >
                {showExperienceFields && (
                  <>
                    <button
                      className="flex ml-auto text-xs text-valencia focus:outline-none"
                      type="button"
                      onClick={() => {
                        if (experience.id) {
                          form.setFieldValue(
                            `users_experiences[${index}]._destroy`,
                            true
                          );
                        } else {
                          remove(index);
                        }
                      }}
                    >
                      <RemoveCircle className="mr-1" fontSize="small" />
                      Remove
                    </button>
                    <Field
                      name={`users_experiences[${index}].company`}
                      component={TextInput}
                      placeholder="Company / organisation"
                    />
                    <Field
                      name={`users_experiences[${index}].position`}
                      component={TextInput}
                      placeholder="Role / position"
                    />
                    <div className="flex flex-col sm:flex-row sm:space-x-4">
                      <Field
                        className="flex-1"
                        name={`users_experiences[${index}].year_start`}
                        component={SelectInput}
                        listHeight={150}
                        options={YEAR_OPTIONS}
                        placeholder="Start year"
                      />
                      <Field
                        className="flex-1"
                        name={`users_experiences[${index}].year_end`}
                        component={SelectInput}
                        listHeight={150}
                        options={[
                          { label: 'Present', value: ' ' },
                          ...YEAR_OPTIONS,
                        ]}
                        placeholder="End year"
                      />
                    </div>
                  </>
                )}
                {showWebsite && (
                  <SocialMediaLinkInput
                    className="w-full"
                    name={`users_experiences[${index}].company_url`}
                    placeholder="Website / portfolio URL"
                  />
                )}
              </div>
            );
          })}
          {showExperienceFields && (
            <button
              className="text-easter flex items-center focus:outline-none"
              type="button"
              onClick={() =>
                push({
                  company: '',
                  company_url: '',
                  position: '',
                  year_start: '',
                  year_end: '',
                })
              }
            >
              <AddCircle className="mr-2" />
              Add work experience
            </button>
          )}
        </>
      )}
    />
  </div>
);

WorkExperienceFields.propTypes = {
  showExperienceFields: PropTypes.bool.isRequired,
  showWebsite: PropTypes.bool.isRequired,
};

const WorkStep = ({
  classificationOptions,
  fieldKeys,
  isLoadingClassifications,
  isLoadingTargetMarkets,
  onIndustryAreasChange,
}) => {
  const { values } = useFormikContext();
  const show = (key) => fieldKeys.includes(key);
  const isCreative = isCreativeProfile({
    industry_areas: values.industry_areas,
  });
  const showServices = fieldKeys.includes('services');
  const showRepArtists = fieldKeys.includes('rep_artists') && isCreative;
  const showRepEvents = fieldKeys.includes('rep_events');
  const showRepVenues = fieldKeys.includes('rep_venues');
  const showRepresentedFields =
    showRepArtists || showRepEvents || showRepVenues;
  const showExperienceFields = show('work_experience');
  const showWebsite = show('website');
  const showExperiences = showExperienceFields || showWebsite;

  return (
    <div>
      {show('industry_areas') && (
        <MultiSelectField
          name="industry_areas"
          label="Areas of the music industry (up to 3)"
          max={3}
          options={classificationOptions.industry_areas}
          placeholder="Select industry areas"
          creatable={false}
          isLoading={isLoadingClassifications}
          onChange={onIndustryAreasChange}
        />
      )}
      {show('professional_goals') && (
        <MultiSelectField
          name="professional_goals"
          label="What do you hope to get from GLP? (up to 3)"
          max={3}
          options={classificationOptions.professional_goals}
          placeholder="Select your goals"
          creatable={false}
          isLoading={isLoadingClassifications}
        />
      )}
      {show('target_markets') && (
        <MultiSelectField
          name="target_markets"
          label="Markets you are interested in (up to 5)"
          max={5}
          options={classificationOptions.target_markets}
          placeholder="Select target markets"
          creatable={false}
          isLoading={isLoadingTargetMarkets}
        />
      )}
      {show('professional_skills') && (
        <MultiSelectField
          name="professional_skills"
          label="Opportunities you are open to"
          exclusiveValue="Not currently looking"
          options={classificationOptions.professional_skills}
          placeholder="Select opportunities"
          creatable={false}
          isLoading={isLoadingClassifications}
        />
      )}
      {show('professional_roles') && isCreative && (
        <MultiSelectField
          name="professional_roles"
          label="Creative roles (up to 3)"
          max={3}
          options={classificationOptions.professional_roles}
          placeholder="Select creative roles"
          creatable={false}
          isLoading={isLoadingClassifications}
        />
      )}
      {show('interest_topics') && isCreative && (
        <MultiSelectField
          name="interest_topics"
          label="Genres you work in (up to 5)"
          max={5}
          options={classificationOptions.interest_topics}
          placeholder="Select genres"
          creatable={false}
          isLoading={isLoadingClassifications}
        />
      )}
      {showServices && <ServicesFields />}
      {showExperiences && (
        <WorkExperienceFields
          showExperienceFields={showExperienceFields}
          showWebsite={showWebsite}
        />
      )}
      {showRepresentedFields && (
        <div className="bg-concrete rounded-lg p-5">
          <p className="font-semibold text-ebony mb-1">Who do you represent?</p>
          <p className="text-xs text-dusty mb-4">
            These fields are optional. Adding them will improve your profile.
          </p>
          {showRepArtists && (
            <MultiSelectField
              name="rep_artists"
              label="Artists represented"
              options={[]}
              placeholder="Search or add artists represented"
            />
          )}
          {showRepEvents && (
            <MultiSelectField
              name="rep_events"
              label="Events represented"
              options={[]}
              placeholder="Search or add events represented"
            />
          )}
          {showRepVenues && (
            <MultiSelectField
              name="rep_venues"
              label="Venues represented"
              options={[]}
              placeholder="Search or add venues represented"
            />
          )}
        </div>
      )}
    </div>
  );
};

WorkStep.propTypes = {
  classificationOptions: PropTypes.object.isRequired,
  fieldKeys: PropTypes.array.isRequired,
  isLoadingClassifications: PropTypes.bool,
  isLoadingTargetMarkets: PropTypes.bool,
  onIndustryAreasChange: PropTypes.func,
};

WorkStep.defaultProps = {
  isLoadingClassifications: false,
  isLoadingTargetMarkets: false,
  onIndustryAreasChange: () => {},
};

export default WorkStep;
